import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Pagination, Spinner, Alert, Badge, Row, Col, Container, Form, Button, Tabs, Tab, Collapse, Modal, ListGroup } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';
import apiService, { makeAuthenticatedRequest } from '../services/apiService';
import Breadcrumb from '../components/Breadcrumb';
import { FaCalendarAlt, FaSync } from 'react-icons/fa';
import './test.css';

// API base URL for direct requests
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000/api";

// Function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Currency formatter used in summaries tabs
const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `$${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const SalesPersons = () => {
    const navigate = useNavigate();
    const [salespersons, setSalespersons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalSalespersons, setTotalSalespersons] = useState(0);
    const pageSize = 50; // Define page size

    // Filter states (align with SalesPersonCommissions.js)
    const [showFilters, setShowFilters] = useState(false);
    const [dateFilter, setDateFilter] = useState('current_month');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [month, setMonth] = useState('');
    const [appliedFilters, setAppliedFilters] = useState({
        dateFilter: 'current_month',
        startDate: '',
        endDate: '',
        month: ''
    });

    // Aggregated totals
    const [aggregatedTotalCommission, setAggregatedTotalCommission] = useState(0);
    const [aggregatedTotalCount, setAggregatedTotalCount] = useState(0);
    // New: processors and transaction coordinators
    const [processors, setProcessors] = useState([]);
    const [transactionCoordinators, setTransactionCoordinators] = useState([]);
    // Summaries collapse state
    const [showSummaries, setShowSummaries] = useState(false);
    // Email modal state
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [modalSelectedIds, setModalSelectedIds] = useState([]);
    const [sendingEmails, setSendingEmails] = useState(false);
    // Modal scroll hint
    const scrollContainerRef = useRef(null);
    const [showScrollHint, setShowScrollHint] = useState(false);

    // Breadcrumb items
    const breadcrumbItems = [
      { label: 'Commissions', href: null},
      { label: 'Reports', href: null},
      { label: 'Sales Person', href: null }
  ];
const agents = [
{ name: "JoAnna Fahey", rate: "1%", status: "active" },
{ name: "Whitney Cox", rate: "1%", status: "active" },
{ name: "Natalie Colon", rate: "1%", status: "active" },
{ name: "Annette Vogdes", rate: "1%", status: "active" },
];
    // Initialize current month
    useEffect(() => {
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setMonth(currentMonth);
        setAppliedFilters((prev) => ({ ...prev, month: currentMonth }));
    }, []);

    // Fetch salespersons data with filters
    const fetchSalespersons = useCallback(async (page = 1, filters = appliedFilters) => {
        try {
            setLoading(true);
            setError(null);
            
            // Build query params
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString(),
                limit: pageSize.toString(),
            });

            // Add date filters
            if (filters?.dateFilter === 'month' && filters?.month) {
                params.append('month', filters.month);
            } else if (filters?.dateFilter === 'date_range' && filters?.startDate && filters?.endDate) {
                params.append('start_date', filters.startDate);
                params.append('end_date', filters.endDate);
            }
            // For current_month/no filter: backend will default to current month

            const response = await makeAuthenticatedRequest(
                `${API_BASE_URL}/automation/salespersons/?${params.toString()}`
            );
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            
            if (data) {
                setSalespersons(data.results || []);
                setTotalSalespersons(data.count || 0);
                setTotalPages(Math.ceil((data.count || 0) / pageSize));
                setAggregatedTotalCommission(data.total_commission || 0);
                setAggregatedTotalCount(data.total_count || 0);
                // Populate processors and transaction coordinators
                setProcessors(data.processors || []);
                setTransactionCoordinators(data.transaction_coordinators || []);
            } else {
                setSalespersons([]);
                setTotalSalespersons(0);
                setTotalPages(1);
                setAggregatedTotalCommission(0);
                setAggregatedTotalCount(0);
                setProcessors([]);
                setTransactionCoordinators([]);
            }
        } catch (err) {
            console.error('Error fetching salespersons:', err);
            setError('Failed to load salespersons data. Please try again.');
            setSalespersons([]);
            setTotalSalespersons(0);
            setTotalPages(1);
            setAggregatedTotalCommission(0);
            setAggregatedTotalCount(0);
            setProcessors([]);
            setTransactionCoordinators([]);
        } finally {
            setLoading(false);
        }
    }, [pageSize, appliedFilters]);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchSalespersons(page, appliedFilters);
    };

    // Handle row click
    const handleRowClick = (id) => {
        // Pass current applied filters via query params to commissions page
        const params = new URLSearchParams();
        if (appliedFilters?.dateFilter) {
            params.set('dateFilter', appliedFilters.dateFilter);
        }
        if (appliedFilters?.dateFilter === 'month' && appliedFilters?.month) {
            params.set('month', appliedFilters.month);
        } else if (
            appliedFilters?.dateFilter === 'date_range' &&
            appliedFilters?.startDate &&
            appliedFilters?.endDate
        ) {
            params.set('startDate', appliedFilters.startDate);
            params.set('endDate', appliedFilters.endDate);
        }
        navigate(`/salespersons/${id}/commissions?${params.toString()}`);
    };

    // Apply filters
    const handleApplyFilters = () => {
        const newFilters = { dateFilter, startDate, endDate, month };
        setAppliedFilters(newFilters);
        setCurrentPage(1);
        fetchSalespersons(1, newFilters);
    };

    // Reset filters
    const handleResetFilters = () => {
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setDateFilter('current_month');
        setStartDate('');
        setEndDate('');
        setMonth(currentMonth);
        const resetFilters = { dateFilter: 'current_month', startDate: '', endDate: '', month: currentMonth };
        setAppliedFilters(resetFilters);
        setCurrentPage(1);
        fetchSalespersons(1, resetFilters);
    };

    // Add refresh handler to support Refresh button in filters
    const handleRefresh = () => {
        fetchSalespersons(currentPage, appliedFilters);
    };

    // Initial data fetch
    useEffect(() => {
        fetchSalespersons(currentPage, appliedFilters);
    }, [fetchSalespersons, currentPage, appliedFilters]);

    // Scroll hint visibility management for modal list
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const updateHint = () => {
            const overflowing = el.scrollHeight > el.clientHeight;
            const atBottom = el.scrollTop >= (el.scrollHeight - el.clientHeight - 1);
            setShowScrollHint(overflowing && !atBottom);
        };
        // Initialize on open and when data changes
        updateHint();
        el.addEventListener('scroll', updateHint);
        return () => el.removeEventListener('scroll', updateHint);
    }, [salespersons, showEmailModal]);
    // Modal selection helpers
    const getSelectableIds = () => salespersons.filter(sp => !!sp.email).map(sp => sp.id);
    const toggleModalSelectAll = () => {
        const selectable = getSelectableIds();
        if (modalSelectedIds.length === selectable.length && selectable.length > 0) {
            setModalSelectedIds([]);
        } else {
            setModalSelectedIds(selectable);
        }
    };

    const toggleModalSelectOne = (id) => {
        setModalSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleModalSendEmails = async () => {
        if (!modalSelectedIds.length) {
            // alert('Please select at least one salesperson.');
            return;
        }
        setSendingEmails(true);
        try {
            const payload = { salesperson_ids: modalSelectedIds };
            if (appliedFilters?.dateFilter === 'month' && appliedFilters?.month) {
                payload.month = appliedFilters.month;
            } else if (appliedFilters?.dateFilter === 'date_range' && appliedFilters?.startDate && appliedFilters?.endDate) {
                payload.start_date = appliedFilters.startDate;
                payload.end_date = appliedFilters.endDate;
            }

            const resp = await makeAuthenticatedRequest(`${API_BASE_URL}/automation/commissions/send-emails/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok) {
                throw new Error(data?.message || `HTTP ${resp.status}`);
            }
            const resultItems = Array.isArray(data?.results) ? data.results : [];
            const successItems = resultItems.filter(r => r.success);
            const successEmails = successItems.map(r => {
                if (r?.email) return r.email;
                const id = r?.salesperson_id ?? r?.id;
                const sp = salespersons.find(s => s.id === id);
                return sp?.email || sp?.name || 'Unknown';
            });
            const emailList = successEmails.filter(Boolean).join(', ');
            Swal.fire({
                icon: 'success',
                title: 'Emails Sent',
                text: `Successfully sent to: ${emailList}${data?.period ? ` (Period: ${data.period})` : ''}.`,
                confirmButtonText: 'OK'
            });
            setModalSelectedIds([]);
            setShowEmailModal(false);
        } catch (e) {
            console.error('Send emails failed:', e);
            Swal.fire({
                icon: 'error',
                title: 'Failed to Send',
                text: e.message || 'Failed to send emails'
            });
        } finally {
            setSendingEmails(false);
        }
    };

    // Get filter description
    const getFilterDescription = () => {
        const { dateFilter, startDate, endDate, month } = appliedFilters;
        if (dateFilter === 'current_month') {
            return 'Current Month';
        } else if (dateFilter === 'month' && month) {
            try {
                const monthDate = new Date(month + '-01');
                return monthDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
            } catch {
                return month;
            }
        } else if (dateFilter === 'date_range' && startDate && endDate) {
            return `${formatDate(startDate)} - ${formatDate(endDate)}`;
        }
        return 'All Time';
    };

    return (
        <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Breadcrumb */}
             <style jsx>{`
        .page-title { color: #2c3e50; font-weight: 700; font-size: 2rem; }
        .page-subtitle { font-size: 1rem; color: #6c757d; }
        .main-card { border: none; border-radius: 20px; box-shadow: 0 10px 40px rgba(0,0,0,0.1); }
        .card-header-custom { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; padding: 1.25rem 1.5rem; }
        .filter-controls { background: rgba(255,255,255,0.95); border-radius: 12px; padding: 1rem; box-shadow: 0 4px 20px rgba(0,0,0,0.08); border: 1px solid rgba(255,255,255,0.2); }
        
        /* Table column sizing tuned to SalesPersons data */
        .table-modern { display: inline-table; table-layout: auto; width: auto; margin-bottom: 0; border-collapse: separate; }
        .table-modern thead th { background-color: #f8f9fa; border: none; font-weight: 800; color: #495057; padding: 11px 0.8rem; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.3px; white-space: nowrap; border-bottom: 2px solid #e9ecef; }
        .table-modern tbody td { font-size: 0.9rem; padding: 12px 15px; border: none; vertical-align: middle; border-bottom: 1px solid #f1f3f4; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .table-modern thead th, .table-modern tbody td { box-sizing: border-box; }
        .table-modern thead th:nth-child(1), .table-modern tbody td:nth-child(1) { min-width: 150px; } /* ID */
        .table-modern thead th:nth-child(2), .table-modern tbody td:nth-child(2) { min-width: 300px; width: max-content; } /* Name */
        .table-modern thead th:nth-child(3), .table-modern tbody td:nth-child(3) { min-width: 140px; } /* Commission% Rate */
        .table-modern thead th:nth-child(4), .table-modern tbody td:nth-child(4) { min-width: 140px; } /* HILI Bonus Rate */
        .table-modern thead th:nth-child(5), .table-modern tbody td:nth-child(5) { min-width: 160px; } /* Bonus Threshold */
        .table-modern thead th:nth-child(6), .table-modern tbody td:nth-child(6) { min-width: 180px; } /* Total Commissions */
        .table-modern thead th:nth-child(7), .table-modern tbody td:nth-child(7) { min-width: 300px; } /* Total Closings */
       
        .loading-container { padding: 3rem 2rem; text-align: center; }
        .table-scroll { width: 100%; display: block; overflow-x: auto !important; overflow-y: hidden; -webkit-overflow-scrolling: touch; }
      `}</style>
            <Breadcrumb items={breadcrumbItems} />

            {/* Page Header */}
            <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="page-title mb-1">
                {'Sales Persons'}
              </h2>
              <p className="page-subtitle text-muted mb-0">
                View salesperson commissions
              </p>
            </div>
            <div className="d-flex align-items-center gap-3">
              <div className="btn-group" role="group">
                <Button 
                  variant={showFilters ? "primary" : "outline-primary"}
                  onClick={() => setShowFilters(!showFilters)}
                  className="d-flex align-items-center gap-2"
                >
                  {showFilters ? 'Filters' : 'Filters'}
                  <i className="fas fa-filter"></i>
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    

 {showFilters && (
        <Card className="mb-4 filter-card" style={{ border: 'none', boxShadow: '0 8px 25px rgba(0,0,0,0.12)', borderRadius: '16px' }}>
          <Card.Header className="card-header-custom">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                                <i className="fas fa-sliders-h me-2"></i>
                <h6 className="mb-0 fw-bold">Filters</h6>
              </div>
            </div>
          </Card.Header>
          <Card.Body style={{ padding: '1rem' }}>
            <Row className="align-items-center g-4 mb-2">
              <Col lg={3}>
                <div className="filter-controls">
                  <label className="form-label fw-semibold text-muted mb-2">Filter Duration</label>
                  <Form.Select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                    <option value="current_month">Current Month</option>
                    <option value="month">Specific Month</option>
                    <option value="date_range">Date Range</option>
                  </Form.Select>
                </div>
              </Col>

              {dateFilter === 'month' && (
                <Col lg={3}>
                  <div className="filter-controls">
                    <label className="form-label fw-semibold text-muted mb-2">Month</label>
                    <Form.Control type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
                  </div>
                </Col>
              )}

              {dateFilter === 'date_range' && (
                <>
                  <Col lg={3}>
                    <div className="filter-controls">
                      <label className="form-label fw-semibold text-muted mb-2">Start Date</label>
                      <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                  </Col>
                  <Col lg={3}>
                    <div className="filter-controls">
                      <label className="form-label fw-semibold text-muted mb-2">End Date</label>
                      <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                  </Col>
                </>
              )}


            </Row>

            {/* Filter Actions */}
            <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
              <Col>
                <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                  <Button variant="primary" className="btn-modern" onClick={handleApplyFilters}>Apply</Button>
                  <Button variant="outline-secondary" className="btn-modern" onClick={handleResetFilters}>Reset</Button>
                  
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
                       {/* Stats Summary */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                                    Total Sales Persons
                                </div>
                                <div className="h4 mb-0 fw-bold">
                                    {totalSalespersons}
                                </div>
                            </div>
                            <div className="ms-3">
                                <i className="fas fa-users fa-2x opacity-75"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100" style={{
                        background: 'linear-gradient(135deg,rgb(184, 101, 156) 0%,rgb(174, 32, 160) 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="d-flex align-items-center"> 
                            <div className="flex-grow-1">
                                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                                     SalesPersons Commissions 
                                </div>
                                <div className="h4 mb-0 fw-bold">
                                    ${Number(aggregatedTotalCommission || salespersons.reduce((sum, sp) => sum + (sp.total_commissions || 0), 0)).toLocaleString()}
                                </div>
                            </div>
                            <div className="ms-3">
                                <i className="fas fa-chart-line fa-2x opacity-75"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            

            

            {/* Main Content Card */}
            <Card className="main-card">
                <Card.Header className="card-header-custom">
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0 fw-bold">Sales Persons</h5>
                        </Col>
                        <Col xs="auto">
                            <Button
                                variant="outline-light"
                                className="d-flex align-items-center gap-2"
                                onClick={() => setShowEmailModal(true)}
                                aria-label="Open Email Modal"
                            >
                                <i className="fas fa-paper-plane"></i> Send Emails
                            </Button>
                        </Col>
                    </Row>
                </Card.Header>
                
                <Card.Body className="p-0">
                   

                    {loading && (
                        <div className="loading-container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                            <Spinner 
                                animation="border" 
                                variant="primary" 
                                className="loading-spinner mb-3"
                            />
                            <h5 className="text-muted">Loading sales persons...</h5>
                            <p className="text-muted mb-0">Please wait while we fetch the latest data</p>
                        </div>
                    )}
                    
                    {error && (
                        <div className="p-4">
                            <Alert variant="danger" className="border-0 rounded-3">
                                <Alert.Heading className="h6">⚠️ Error Loading Data</Alert.Heading>
                                {error}
                            </Alert>
                        </div>
                    )}
                    
                    {!loading && !error && (
                        <>
                            {salespersons.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="table-modern mb-0">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Comm% Rate</th>
                                                <th>HiLi Rate</th>
                                                <th>Bonus Threshold</th>
                                                {/* <th>Email</th> */}
                                                <th>Total Commissions</th>
                                                <th>Total Closings</th>
                                                {/* <th>Created</th>
                                                <th>Updated</th> */}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {salespersons.map((salesperson) => (
                                                <tr 
                                                    key={salesperson.id} 
                                                    onClick={() => handleRowClick(salesperson.id)}
                                                    style={{cursor: 'pointer'}}
                                                >
                                                    <td>
                                                        <span className="id-badge">#{salesperson.id}</span>
                                                    </td>
                                                    <td className="name-cell" title={salesperson.name}>
                                                        {salesperson.name}
                                                    </td>
                                                    <td>
                                                        {typeof salesperson.base_commission === 'number'
                                                            ? `${(salesperson.base_commission * 100)}%`
                                                            : (salesperson.base_commission ?? 'N/A')}
                                                    </td>
                                                    <td>
                                                        {typeof salesperson.bonus === 'number'
                                                            ? `${(salesperson.bonus * 100)}%`
                                                            : (salesperson.bonus ?? 'N/A')}
                                                    </td>
                                                    <td>
                                                        {typeof salesperson.threshold === 'number'
                                                            ? `$${Number(salesperson.threshold).toLocaleString()}`
                                                            : (salesperson.threshold ?? 'N/A')}
                                                    </td>
                                                    
                                                    <td className="commission-cell">
                                                        ${(salesperson.total_commissions || 0).toLocaleString()}
                                                    </td>
                                                    <td>
                                                        <Badge bg="info" className="rounded-pill">
                                                            {salesperson.commission_count || 0}
                                                        </Badge>
                                                    </td>
                                                    {/* <td className="date-cell">
                                                        {formatDate(salesperson.created_at)}
                                                    </td>
                                                    <td className="date-cell">
                                                        {formatDate(salesperson.updated_at)}
                                                    </td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-icon">👥</div>
                                    <h5 className="mb-2">No Sales Persons Found</h5>
                                    <p className="mb-0">
                                        No sales persons have been added to the system yet.
                                    </p>
                                </div>
                            )}
                            
                            {salespersons.length > 0 && (
                                <div className="pagination-container">
                                    <Row className="align-items-center">
                                        <Col>
                                            <div className="pagination-info">
                                                {/* Showing <strong>{salespersons.length}</strong> of <strong>{totalSalespersons}</strong> entries */}
                                            </div>
                                        </Col>
                                        <Col xs="auto">
                                            <Pagination className="mb-0">
                                                <Pagination.First
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={currentPage === 1}
                                                />
                                                <Pagination.Prev 
                                                    onClick={() => handlePageChange(currentPage - 1)} 
                                                    disabled={currentPage === 1}
                                                />
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    // Show 5 pages around current page
                                                    let pageNum;
                                                    const half = Math.floor(5 / 2);
                                                    let start = Math.max(1, currentPage - half);
                                                    let end = Math.min(totalPages, start + 4);
                                                    if (end - start < 4) {
                                                        start = Math.max(1, end - 4);
                                                    }
                                                    pageNum = start + i;
                                                    if (pageNum > end) return null;
                                                    return (
                                                        <Pagination.Item
                                                            key={pageNum}
                                                            active={pageNum === currentPage}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </Pagination.Item>
                                                    );
                                                })}
                                                <Pagination.Next 
                                                    onClick={() => handlePageChange(currentPage + 1)} 
                                                    disabled={currentPage === totalPages}
                                                />
                                                <Pagination.Last 
                                                    onClick={() => handlePageChange(totalPages)} 
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
            {/* Email Selection Modal */}
            <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} size="md" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select Sales Persons to Email</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="text-muted">Filter: {getFilterDescription()}</div>
                        <Button variant="outline-primary" size="sm" onClick={toggleModalSelectAll}>
                            {(() => {
                                const selectableCount = salespersons.filter(sp => !!sp.email).length;
                                return modalSelectedIds.length === selectableCount && selectableCount > 0 ? 'Unselect All' : 'Select All';
                            })()}
                        </Button>
                    </div>
                    {salespersons.length === 0 ? (
                        <div className="text-center text-muted py-4">No sales persons to select.</div>
                    ) : (
                        <div style={{ maxHeight: '320px', overflowY: 'auto' }} ref={scrollContainerRef}>
                            <ListGroup>
                            {salespersons.map(sp => {
                                const hasEmail = !!sp.email;
                                const selected = hasEmail && modalSelectedIds.includes(sp.id);
                                return (
                                    <ListGroup.Item
                                        key={sp.id}
                                        action={hasEmail}
                                        onClick={hasEmail ? () => toggleModalSelectOne(sp.id) : undefined}
                                        className="d-flex justify-content-between align-items-center"
                                        style={hasEmail ? undefined : { opacity: 0.6, cursor: 'not-allowed', pointerEvents: 'none' }}
                                        variant={selected ? 'success' : undefined}
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            {selected ? <i className="fas fa-check-circle text-success"></i> : <i className="far fa-circle text-muted"></i>}
                                            <div className="d-flex flex-column">
                                                <span className="fw-semibold">{sp.name}</span>
                                                <small className="text-muted">{hasEmail ? sp.email : 'No email'}</small>
                                            </div>
                                        </div>
                                        <span className="fw-semibold">{`$${Number(sp.total_commissions || 0).toLocaleString()}`}</span>
                                    </ListGroup.Item>
                                );
                            })}
                            </ListGroup>
                            {showScrollHint && (
                                <div style={{ position: 'sticky', bottom: 0, width: '100%', display: 'flex', justifyContent: 'center', background: 'linear-gradient(to top, rgba(255,255,255,0.95), rgba(255,255,255,0))', padding: '6px' }}>
                                    <small className="text-muted d-flex align-items-center gap-2">
                                        <i className="fas fa-arrow-down"></i> Scroll for more
                                    </small>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEmailModal(false)}>Cancel</Button>
                    <Button
                        variant="success"
                        className="d-flex align-items-center gap-2"
                        onClick={handleModalSendEmails}
                        disabled={sendingEmails || modalSelectedIds.length === 0}
                    >
                        {sendingEmails ? (
                            <>
                                <Spinner animation="border" size="sm" /> Sending Emails...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-paper-plane"></i> Send Emails
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default SalesPersons;